import { useRef, useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useTemplateContext } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// Background image slot group, mirroring HeroSolidBG's `featuredImageDef` so the
// hyperlocal banner renders the same parallax + Ken Burns photo treatment as the
// rest of the site's page heroes.
const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Page hero image' },
  },
};

// Escape user-supplied plain text before injecting it into a richtext default.
// Hyperlocal h1_title / h2_subtitle are stored as plain strings; wrapping them
// in <p>…</p> keeps the same DOM shape RichTextContent expects, but we must
// neutralize any HTML special characters so a user-typed `<` doesn't poison
// the markup.
function toRichTextDefault(text: string | undefined | null, fallback: string): string {
  if (!text) return fallback;
  const escaped = String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<p>${escaped}</p>`;
}

// The hyperlocal generator stores "no value" as the literal strings "false",
// "null", "none", or empty — none of which are valid image URLs but all are
// truthy in JS. Treat them as missing so the fallback chain advances.
function pickImage(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  if (lower === 'false' || lower === 'true' || lower === 'null' || lower === 'none' || lower === 'undefined') return null;
  return trimmed;
}

export function HyperlocalHero() {
  const ctx = useTemplateContext();
  const hl = ctx?.hyperlocal as Record<string, string | null | undefined> | undefined;

  const bgImage = useSlotGroup('hyperlocalhero.bg_image', bgImageDef);

  // Image priority: explicit slot-group override > hyperlocal item's
  // featured_image > nothing (solid primary placeholder). pickImage() filters
  // out generator-emitted sentinel strings ("false", "null", etc.).
  const displayImage = pickImage(bgImage.image) || pickImage(hl?.featured_image) || '';
  const displayAlt = bgImage.alt || hl?.h1_title || 'Page hero image';

  const titleDefault = toRichTextDefault(hl?.h1_title, '<p>Hyperlocal Page Title</p>');
  const subtitleDefault = toRichTextDefault(hl?.h2_subtitle, '<p>Explore apartments in your area</p>');

  // Parallax refs — the background image shifts at 30 % of scroll speed to
  // create depth, matching HeroSolidBG.
  const sectionRef = useRef<HTMLElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;
    // Respect reduced-motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      const scrolled = -rect.top;
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      imgWrap.style.transform = `translateY(${scrolled * 0.3}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // seed the position on mount (handles pre-scrolled pages)
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="relative w-full z-10 bg-[var(--vibe-color-background)]">
      <section
        ref={sectionRef}
        className="relative w-full h-[98vh] min-h-[720px] text-white"
        style={{
          // Downward-pointing chevron: the bottom edge slopes from each side
          // down to a single point at the horizontal centre, revealing the
          // white page background as two triangular wedges. clip-path also
          // clips the oversized parallax image, replacing overflow-hidden.
          //
          // Height trimmed (was 105vh / min 760px, briefly 90vh / 680px) so
          // the section clears the viewport a touch sooner and the next
          // section rises above the fold. The
          // dip was deepened to match the home Hero (sides 76%, tip 90% → 14%,
          // was 10%) so the wedge stays crisp — slightly steeper — at the
          // shorter height. Centered content clears it: the deepest cut under
          // the (horizontally centred) copy is still the tip at 90%, and the
          // 22vh bottom padding below keeps the last line clear of it.
          clipPath: 'polygon(0 0, 100% 0, 100% 76%, 50% 90%, 0 76%)',
        }}
      >
        {/* Parallax + Ken Burns animated background */}
        <EditableSlotGroup groupPath="hyperlocalhero.bg_image" definition={bgImageDef}>
          {/* Oversized wrapper: 140 % tall starting 20 % above the section top,
              and ~2 % wider than the section (1 % bleed on each side). The
              extra height gives the parallax translate room to move, and the
              extra width absorbs the Ken Burns horizontal translate. */}
          <div
            ref={imgWrapRef}
            className="absolute h-[140%]"
            style={{ top: '-20%', left: '-1%', right: '-1%', willChange: 'transform' }}
          >
            {displayImage ? (
              <VibesitesImage
                url={displayImage}
                alt={displayAlt}
                priority
                sizes="100vw"
                className="w-full h-full object-cover"
                style={{
                  animation: 'heroSolidKenBurns 30s ease-in-out infinite alternate',
                  willChange: 'transform',
                  transformOrigin: 'bottom right',
                  imageRendering: 'high-quality' as any,
                }}
              />
            ) : (
              <div className="w-full h-full bg-[var(--vibe-color-primary)]" />
            )}
          </div>
        </EditableSlotGroup>

        {/* Primary-color overlay at 77% opacity, layered above the parallax
            image but below the content (z-10) so text stays legible. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 z-0 pointer-events-none"
          style={{ background: 'var(--vibe-color-primary)', opacity: 0.77 }}
        />

        {/* Content — vertically centred over the image, matching HeroSolidBG.
            The top padding reserves room for the fixed header (and offer
            banner when present) so the centred title never tucks under the
            navigation menu. The bottom padding keeps the last line of copy
            clear of the chevron clip-path (which cuts up to 20% off the
            bottom edge) so the blurb never gets cut off. */}
        <div
          className="relative z-10 flex flex-col items-center justify-center h-full text-center px-6 sm:px-8 pointer-events-none"
          style={{
            paddingTop: 'calc(var(--offer-banner-height, 0px) + 14rem)',
            paddingBottom: '22vh',
          }}
        >
          {/* Page Title */}
          <div className="min-w-0 w-full pointer-events-auto">
            <RichTextContent
              path="hyperlocalhero.title"
              label="Title"
              defaultValue={titleDefault}
              as="h1"
              className="hero-solid-title-font text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal uppercase tracking-wider leading-tight break-words text-center drop-shadow-lg"
            />
          </div>

          {/* Subtitle / blurb */}
          <RichTextContent
            path="hyperlocalhero.subtitle"
            label="Subtitle"
            defaultValue={subtitleDefault}
            as="div"
            className="hero-solid-blurb-font text-base font-normal not-italic leading-relaxed opacity-90 [&_a]:underline text-center mt-6 max-w-3xl drop-shadow-md pointer-events-auto"
          />
        </div>

        <style>{`
          /* Match FloorplansList's feature_title: Oswald at normal weight,
             applied to the element and all of its rich-text children. */
          .hero-solid-title-font,
          .hero-solid-title-font * {
            font-family: 'Oswald', 'Arial Narrow', system-ui, sans-serif;
            font-weight: 400;
          }
          /* Match FloorplansList's feature_body (.fp-feature-body-font):
             Space Mono at normal weight/style, applied to the element and
             all of its rich-text children. */
          .hero-solid-blurb-font,
          .hero-solid-blurb-font * {
            font-family: 'Space Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
            font-weight: 400;
            font-style: normal;
            /* Pin the blurb (and every rich-text child) to a single 16px so
               it renders identically on every inner-page header and stays
               small enough that a multi-line property blurb never spills into
               the clipped chevron at the bottom of the section. */
            font-size: 1rem;
          }
          @keyframes heroSolidKenBurns {
            0% { transform: scale(1.01) translate(0, 0); }
            100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
          }
        `}</style>
      </section>

      {/* Vertical accent line centred on the chevron tip — the clip-path dips
          to its lowest point (90% of the hero height) at the horizontal centre,
          which is exactly where the image meets the white space along this
          column. Lives in the wrapper (not the clipped section) so the chevron
          clip-path doesn't cut it off. */}
      <div
        aria-hidden="true"
        className="absolute left-1/2 -translate-x-1/2 w-px h-[16%] z-20"
        style={{ background: 'var(--vibe-color-secondary)', bottom: '2%' }}
      />
    </div>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HyperlocalHero',
  displayName: 'Hyperlocal Hero',
  tokens: {
    'hyperlocalhero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Hyperlocal Page Title</p>',
    },
    'hyperlocalhero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Explore apartments in your area</p>',
    },
  },
  slotGroups: {
    'hyperlocalhero.bg_image': {
      label: 'Background Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultValue: '' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Page hero image' },
      },
    },
  },
};

(HyperlocalHero as any).contentSchema = contentSchema;

export default HyperlocalHero;
